import { useRef, useState, useEffect, useCallback } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { ExplorerItem } from "@titancloud/shared";
import { FileCard } from "./FileCard";
import { ContextMenu, type ContextMenuItem } from "../ui/ContextMenu";
import { downloadFile } from "../../api/files.api";
import { downloadFolderZip } from "../../api/folders.api";
import { downloadAdminExplorerFile } from "../../api/admin.api";

const CARD_MIN_WIDTH = 120;
const GAP = 12;
const ROW_HEIGHT = 176;
const OVERSCAN = 4;

interface ContextMenuState {
  x: number;
  y: number;
  item: ExplorerItem;
}

interface VirtualFileGridProps {
  items: ExplorerItem[];
  onOpen: (id: string) => void;
  onRename: (id: string, kind: "file" | "folder", name: string) => void;
  onDelete: (id: string, kind: "file" | "folder", name: string) => void;
  readOnly?: boolean;
  explorerUserId?: string;
  onFolderNavigate?: (folderId: string) => void;
  isCreatingFolder?: boolean;
  creatingFolderName?: string;
  onDownload?: (fileId: string, fileName: string) => void;
  onAdminErase?: (fileId: string, fileName: string) => void;
  /** When provided, the grid fetches the next page itself as the user nears the bottom. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

const LOAD_MORE_ROW_THRESHOLD = 3;

export function VirtualFileGrid({
  items,
  onOpen,
  onRename,
  onDelete,
  readOnly,
  explorerUserId,
  onFolderNavigate,
  isCreatingFolder = false,
  creatingFolderName = "",
  onDownload,
  onAdminErase,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
}: VirtualFileGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry!.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cols = containerWidth > 0
    ? Math.max(1, Math.floor((containerWidth + GAP) / (CARD_MIN_WIDTH + GAP)))
    : 0;

  const rowCount = cols > 0 ? Math.ceil(items.length / cols) : 0;

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => containerRef.current,
    estimateSize: useCallback(() => ROW_HEIGHT + GAP, []),
    overscan: OVERSCAN,
  });

  // Infinite scroll: fetch the next page once the last rendered row nears the bottom.
  const virtualRows = virtualizer.getVirtualItems();
  useEffect(() => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;
    const lastRow = virtualRows[virtualRows.length - 1];
    if (lastRow && lastRow.index >= rowCount - 1 - LOAD_MORE_ROW_THRESHOLD) {
      onLoadMore();
    }
  }, [virtualRows, rowCount, hasNextPage, isFetchingNextPage, onLoadMore]);

  const handleContextMenu = (e: React.MouseEvent, item: ExplorerItem) => {
    e.preventDefault();
    if (readOnly && item.kind === "folder") return;
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  const downloadIcon = (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z" />
      <path d="M7.646 11.854a.5.5 0 00.708 0l3-3a.5.5 0 00-.708-.708L8.5 10.293V1.5a.5.5 0 00-1 0v8.793L5.354 8.146a.5.5 0 10-.708.708l3 3z" />
    </svg>
  );

  const eraseIcon = (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z" />
      <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 110-2h3.5l1-1h3l1 1h3.5a1 1 0 011 1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118z" clipRule="evenodd" />
    </svg>
  );

  const getContextMenuItems = (item: ExplorerItem): ContextMenuItem[] => {
    if (readOnly && explorerUserId && item.kind === "file") {
      const menuItems: ContextMenuItem[] = [
        {
          label: "Download",
          onClick: () => onDownload
            ? onDownload(item.id, item.name)
            : void downloadAdminExplorerFile(explorerUserId, item.id, item.name),
          icon: downloadIcon,
        },
      ];
      if (onAdminErase) {
        menuItems.push({
          label: "Erase from S3",
          onClick: () => onAdminErase(item.id, item.name),
          danger: true,
          divider: true,
          icon: eraseIcon,
        });
      }
      return menuItems;
    }
    return [
      {
        label: "Rename",
        onClick: () => setRenameId(item.id),
        icon: (
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
      },
      {
        label: item.kind === "folder" ? "Download as ZIP" : "Download",
        onClick: () => {
          if (item.kind === "folder") downloadFolderZip(item.id, item.name);
          else void downloadFile(item.id, item.name);
        },
        icon: downloadIcon,
      },
      {
        label: "Delete",
        onClick: () => onDelete(item.id, item.kind, item.name),
        danger: true,
        divider: true,
        icon: (
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z" />
            <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 110-2h3.5l1-1h3l1 1h3.5a1 1 0 011 1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118z" clipRule="evenodd" />
          </svg>
        ),
      },
    ];
  };

  return (
    <>
      <div ref={containerRef} className="flex-1 overflow-y-auto overflow-x-hidden p-4">
        {/* Ghost card row — shown while a folder is being created */}
        {isCreatingFolder && containerWidth > 0 && (
          <div
            className="mb-3"
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gap: GAP,
            }}
          >
            <div className="animate-pulse flex flex-col items-center gap-2 rounded-xl border border-gray-700 bg-gray-800/60 p-3">
              {/* Preview area */}
              <div className="flex h-20 w-full items-center justify-center rounded-lg bg-gray-700/60">
                <svg className="h-10 w-10 text-yellow-400/40" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                </svg>
              </div>
              {/* Name */}
              <div className="flex h-8 w-full items-center justify-center px-1">
                {creatingFolderName ? (
                  <span className="line-clamp-2 w-full break-all text-center text-xs leading-tight text-gray-500">
                    {creatingFolderName}
                  </span>
                ) : (
                  <div className="h-3 w-16 rounded bg-gray-700" />
                )}
              </div>
              {/* Spinner */}
              <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-600 border-t-gray-400 animate-spin" />
            </div>
          </div>
        )}
        <div style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}>
          {virtualRows.map((virtualRow) => {
            const startIdx = virtualRow.index * cols;
            const rowItems = items.slice(startIdx, startIdx + cols);

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: virtualRow.start,
                  left: 0,
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  gap: GAP,
                  paddingBottom: GAP,
                }}
              >
                {rowItems.map((item) => (
                  <div
                    key={item.id}
                    style={{ height: ROW_HEIGHT }}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                  >
                    <FileCard
                      item={item}
                      onOpen={() => onOpen(item.id)}
                      onRename={(name) => onRename(item.id, item.kind, name)}
                      onDelete={() => onDelete(item.id, item.kind, item.name)}
                      forceRename={!readOnly && renameId === item.id}
                      onForceRenameDone={() => setRenameId(null)}
                      {...(readOnly ? { readOnly: true as const } : {})}
                      {...(explorerUserId ? { explorerUserId } : {})}
                      {...(onFolderNavigate ? { onFolderNavigate } : {})}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        {isFetchingNextPage && (
          <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500">
            <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-600 border-t-gray-400 animate-spin" />
            Loading more…
          </div>
        )}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems(contextMenu.item)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}
